import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/features/Auth/lib/useAuth'
import { Button } from '@/shared/components/common/Button/Button'
import Popup from '@/shared/components/layers/Popup/Popup'
import css from './AutumnUpdatePopup.module.css'

const UPDATE_VERSION = 'autumn-2026-v4'
const CHUGUNOV_UPDATE_VERSION = 'autumn-2026-v4-chugunov-refresh-1'
const MAX_VIEWS = 3

type UpdateState = { views: number; dismissed: boolean }

function storageKey(userId: number, version: string) {
  return `sales-plan-update:${version}:${userId}`
}

function readState(userId: number, version: string): UpdateState {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey(userId, version)) || '{}')
    return {
      views: Number.isFinite(Number(parsed.views)) ? Math.max(0, Number(parsed.views)) : 0,
      dismissed: parsed.dismissed === true,
    }
  } catch {
    return { views: 0, dismissed: false }
  }
}

function saveState(userId: number, version: string, state: UpdateState) {
  try {
    localStorage.setItem(storageKey(userId, version), JSON.stringify(state))
  } catch {
    // При недоступном localStorage попап остаётся рабочим в текущей сессии.
  }
}

function UpdateMedia({ src, alt, fallback }: { src: string; alt: string; fallback: string }) {
  const [failed, setFailed] = useState(false)
  if (failed) return <div className={css.mediaFallback}>{fallback}</div>
  return <img className={css.media} src={src} alt={alt} onError={() => setFailed(true)} />
}

export default function AutumnUpdatePopup() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const userId = Number(user?.id || user?.bx?.bx_user?.bx_id || 0)
  const userEmail = (user?.email || user?.bx?.bx_user?.email || '').trim().toLowerCase()
  const updateVersion = userEmail === 'chugunovis@okgnn.ru' ? CHUGUNOV_UPDATE_VERSION : UPDATE_VERSION
  const initialState = useMemo(
    () => userId ? readState(userId, updateVersion) : { views: MAX_VIEWS, dismissed: true },
    [userId, updateVersion]
  )
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!userId || initialState.dismissed || initialState.views >= MAX_VIEWS) return
    saveState(userId, updateVersion, { ...initialState, views: initialState.views + 1 })
    setOpen(true)
  }, [userId, updateVersion, initialState])

  const dismissForever = () => {
    if (userId) saveState(userId, updateVersion, { views: MAX_VIEWS, dismissed: true })
    setOpen(false)
  }

  const openMethodology = () => {
    setOpen(false)
    navigate('/SalePlan/methodology')
  }

  return <Popup open={open} onClose={() => setOpen(false)} title="Осеннее обновление плана продаж" size="xl">
    <div className={css.intro}>
      Аналитика стала подробнее: теперь результат можно разбирать по компании, отделу и сотруднику.
    </div>

    <div className={css.showcase}>
      <article className={`${css.feature} ${css.analyticsFeature}`}>
        <UpdateMedia
          src="/updates/autumn-sales-plan-overview.gif?v=4"
          alt="Графики и управленческая аналитика плана продаж"
          fallback="Демонстрация графиков и управленческой аналитики"
        />
        <div className={css.featureBody}>
          <h3>Управленческая аналитика</h3>
          <p>Темп выполнения, прогноз, рабочая воронка, регулярность отгрузок, маржинальность и детализация до конкретной сделки.</p>
        </div>
      </article>

    </div>

    <div className={css.points}>
      <span>Графики на весь экран и сравнение с прошлым годом</span>
      <span>Краткий отчёт: что влияет на выполнение плана</span>
      <span>Аналитика срезов и поиск изменившихся сделок</span>
      <span>Краткая методика расчёта у каждой новой метрики</span>
      <span>Персональный PIN для доступа к планам</span>
    </div>

    <div className={css.actions}>
      <Button variant="secondary" onClick={dismissForever}>Больше не показывать</Button>
      <Button variant="secondary" onClick={openMethodology}>Как считаются показатели</Button>
      <Button onClick={() => setOpen(false)}>Понятно</Button>
    </div>
  </Popup>
}
